// App-Rahmen: linke Navigation, Kopfzeile, Telefon-Leiste, Inhalt, Erinnerungen, Befehlspalette, globale Tastenkürzel.
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { MetaProvider, useMeta } from "../daten/meta";
import { EMAIL_VERFASSEN, type EmailVerfassenDetail, type SchreibAuftrag } from "../daten/schreiben";
import { SeitenProvider } from "../daten/seite";
import { useOberflaeche } from "../daten/oberflaeche";
import { DialerLeiste } from "../telefonie";
import { T } from "../texte";
import { ebene, tipptGerade } from "../werkzeuge/tasten";
import { Laden } from "./Bausteine";
import { Befehlspalette } from "./Befehlspalette";
import { ErinnerungenHost } from "./ErinnerungenHost";
import { FehlerBox } from "./Fehler";
import { Icon } from "./Icon";
import { Kopfzeile } from "./Kopfzeile";
import { Navigation } from "./Navigation";
import { Tastenkuerzel } from "./Tastenkuerzel";
import { Tooltips } from "./Tooltips";

export function AppLayout() {
  const { nutzer, laedt } = useAuth();
  const ort = useLocation();
  if (laedt) return <div className="laden">{T.laden}</div>;
  if (!nutzer) return <Navigate to="/login" replace state={{ von: ort.pathname + ort.search }} />;
  return (
    <MetaProvider>
      <SeitenProvider>
        <Rahmen />
      </SeitenProvider>
    </MetaProvider>
  );
}

function Rahmen() {
  useOberflaeche(); // lädt Dichte und Countdown des Nutzers, setzt data-dichte am Dokument
  const [palette, setPalette] = useState(false);
  const [tasten, setTasten] = useState(false);
  const { meta, laedt, fehler, neuLaden } = useMeta();
  const { nutzer } = useAuth();
  const ort = useLocation();
  const navigate = useNavigate();
  const [hinweisWeg, setHinweisWeg] = useState(() => {
    try {
      return sessionStorage.getItem("crm-hinweis-schluessel") === "weg";
    } catch {
      return false;
    }
  });
  const ohneSchluessel = nutzer?.close_key?.kann_schreiben === false && !hinweisWeg && !ort.pathname.startsWith("/einstellungen");
  const g = useRef(0);
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((p) => !p);
        return;
      }
      if (tipptGerade(e) || ebene.offen() || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "/") {
        e.preventDefault();
        setPalette(true);
      } else if (e.key === "?") {
        e.preventDefault();
        setTasten(true);
      } else if (e.key === "g") {
        g.current = Date.now();
      } else if (Date.now() - g.current < 800 && (e.key === "i" || e.key === "l")) {
        e.preventDefault();
        g.current = 0;
        navigate(e.key === "i" ? "/inbox" : "/leads");
      }
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [navigate]);

  // Brief-Symbol in Listen und Kontaktkarten: statt mailto die Lead-Seite mit dem CRM-Komposer öffnen (Empfänger
  // vorbelegt). So steht die Mail im Feed und in Close, und der Kalt-Schutz greift.
  useEffect(() => {
    const abnehmen = (e: Event) => {
      const d = (e as CustomEvent<EmailVerfassenDetail>).detail;
      if (!d?.leadId) return;
      e.preventDefault();
      const auftrag: SchreibAuftrag = { schreiben: "email", an: d.an || null, kontakt: d.contactId ?? null };
      navigate(`/lead/${encodeURIComponent(d.leadId)}`, { state: auftrag });
    };
    window.addEventListener(EMAIL_VERFASSEN, abnehmen);
    return () => window.removeEventListener(EMAIL_VERFASSEN, abnehmen);
  }, [navigate]);

  // Desktop-App: Klick auf eine Systemmitteilung navigiert innerhalb der Oberfläche.
  useEffect(() => {
    const d = window.crmDesktop;
    if (!d) return;
    d.beiNavigation((pfad) => navigate(pfad));
    return () => d.beiNavigation(null);
  }, [navigate]);

  return (
    <div className="rahmen">
      <Navigation onTasten={() => setTasten(true)} />
      <div className="haupt">
        <Kopfzeile onSuche={() => setPalette(true)} />
        {/* Telefon-Leiste: fester Platz direkt unter der Kopfzeile, zeigt sich selbst nur bei Gespräch, Sitzung oder Meldung */}
        <DialerLeiste />
        {ohneSchluessel ? (
          <div className="leiste-warnung" role="status">
            <Icon name="warnung" groesse={15} />
            <span>Kein eigener Close-Schlüssel: Status, Felder, Notizen und Aufgaben können nicht nach Close geschrieben werden. Custom Activities werden im CRM gespeichert.</span>
            <Link to="/einstellungen/close-schluessel">Schlüssel eintragen</Link>
            <button
              type="button"
              className="symbolknopf symbolknopf--klein"
              aria-label="Hinweis ausblenden"
              onClick={() => {
                setHinweisWeg(true);
                try {
                  sessionStorage.setItem("crm-hinweis-schluessel", "weg");
                } catch {
                  /* egal */
                }
              }}
            >
              <Icon name="schliessen" groesse={13} />
            </button>
          </div>
        ) : null}
        <main className="inhalt">
          {!meta && laedt ? (
            <Laden text="Stammdaten werden geladen …" />
          ) : !meta && fehler ? (
            <div className="seite">
              <h1>Stammdaten fehlen</h1>
              <p className="seite__text">Status, Felder und Custom-Activity-Typen konnten nicht geladen werden. Ohne sie lässt sich nicht arbeiten.</p>
              <FehlerBox fehler={fehler} onNochmal={() => void neuLaden()} />
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      <ErinnerungenHost />
      <Befehlspalette offen={palette} schliessen={() => setPalette(false)} onTasten={() => setTasten(true)} />
      <Tastenkuerzel offen={tasten} onSchliessen={() => setTasten(false)} />
      <Tooltips />
    </div>
  );
}
